import {
  InquiryDto,
  InquiryPublicDto,
  CreateInquiryDto,
  UpdateInquiryDto,
  InquiryAttachmentDto,
  InquiryNdaAcceptanceDto,
  type RejectInquiryDto,
  type FinalizeEvaluationDto,
} from '@paladin-monorepo/common';
import API from '../api';

async function uploadFile(
  endpoint: string,
  file: File,
  onProgress?: (progress: number) => void,
  description?: string,
  ndaProtected?: boolean
): Promise<InquiryAttachmentDto> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('filename', file.name); // Send original filename
  if (description) {
    formData.append('description', description);
  }
  if (ndaProtected !== undefined) {
    formData.append('ndaProtected', String(ndaProtected));
  }

  return API.post<InquiryAttachmentDto>(endpoint, formData, {
    onUploadProgress: (progress) => {
      if (onProgress) {
        onProgress(progress.progress || 0);
      }
    },
  }).then((res) => res.data);

  // const xhr = new XMLHttpRequest();

  // return new Promise((resolve, reject) => {
  //   xhr.upload.addEventListener('progress', (event) => {
  //     if (event.lengthComputable && onProgress) {
  //       const progress = (event.loaded / event.total) * 100;
  //       onProgress(progress);
  //     }
  //   });

  //   xhr.addEventListener('load', () => {
  //     if (xhr.status >= 200 && xhr.status < 300) {
  //       try {
  //         const response = JSON.parse(xhr.responseText);
  //         resolve(response);
  //       } catch (error) {
  //         reject(new Error('Invalid JSON response'));
  //       }
  //     } else {
  //       reject(new ApiError(`Upload failed: ${xhr.statusText}`, xhr.status));
  //     }
  //   });

  //   xhr.addEventListener('error', () => {
  //     reject(new Error('Upload failed'));
  //   });

  //   xhr.open('POST', `${API_BASE_URL}${endpoint}`);
  //   if (token) {
  //     xhr.setRequestHeader('Authorization', `Bearer ${token}`);
  //   }
  //   xhr.send(formData);
  // });
}

export const inquiriesApi = {
  // Get all inquiries for the current user's account
  async getInquiries(): Promise<InquiryDto[]> {
    return API.get<InquiryDto[]>('/inquiries').then((res) => res.data);
  },

  // Get a single inquiry by ID
  async getInquiry(id: string): Promise<InquiryDto | InquiryPublicDto> {
    return API.get<InquiryDto | InquiryPublicDto>(`/inquiries/${id}`).then((res) => res.data);
  },

  // Create a new inquiry
  async createInquiry(data: CreateInquiryDto): Promise<InquiryDto> {
    return API.post<InquiryDto>('/inquiries', data).then((res) => res.data);
  },

  // Update an existing inquiry
  async updateInquiry(id: string, data: UpdateInquiryDto): Promise<InquiryDto> {
    return API.patch<InquiryDto>(`/inquiries/${id}`, data).then((res) => res.data);
  },

  // Delete an inquiry
  async deleteInquiry(id: string): Promise<void> {
    return API.delete<void>(`/inquiries/${id}`).then((res) => res.data);
  },

  // Ready an inquiry (change status from draft to in review)
  async readyInquiry(id: string, body?: { comment?: string }): Promise<InquiryDto> {
    return API.post<InquiryDto>(`/ready-inquiries/${id}`, body ?? {}).then((res) => res.data);
  },

  // Publish an inquiry (change status from draft to published)
  async publishInquiry(id: string): Promise<InquiryDto> {
    return API.post<InquiryDto>(`/published-inquiries/${id}`).then((res) => res.data);
  },

  // Review actions for an inquiry in review (admin only)
  async rejectInquiryFromReview(id: string, data: RejectInquiryDto): Promise<InquiryDto> {
    return API.post<InquiryDto>(`/review-inquiries/${id}/reject`, data).then((res) => res.data);
  },

  async acceptInquiryFromReview(id: string): Promise<InquiryDto> {
    return API.post<InquiryDto>(`/review-inquiries/${id}/accept`).then((res) => res.data);
  },

  // Finalize evaluation for an inquiry (change status from published to evaluated)
  async finalizeEvaluation(id: string, data: FinalizeEvaluationDto): Promise<InquiryDto> {
    return API.post<InquiryDto>(`/evaluated-inquiries/${id}`, data).then((res) => res.data);
  },

  // Upload an attachment to an inquiry
  async copyAttachmentsFromChatSession(
    inquiryId: string,
    chatSessionId: string,
  ): Promise<InquiryAttachmentDto[]> {
    return API.post<InquiryAttachmentDto[]>(`/inquiries/${inquiryId}/attachments/from-chat-session`, {
      chatSessionId,
    }).then((res) => res.data);
  },

  async uploadAttachment(
    inquiryId: string,
    file: File,
    onProgress?: (progress: number) => void,
    description?: string,
    ndaProtected?: boolean
  ): Promise<InquiryAttachmentDto> {
    return uploadFile(`/inquiries/${inquiryId}/attachments`, file, onProgress, description, ndaProtected);
  },

  // Delete an attachment from an inquiry
  async deleteAttachment(inquiryId: string, attachmentId: string): Promise<void> {
    return API.delete<void>(`/inquiries/${inquiryId}/attachments/${attachmentId}`).then((res) => res.data);
  },

  // Download an attachment from an inquiry
  async downloadAttachment(inquiryId: string, attachmentId: string, filename: string): Promise<void> {
    const response = await API.get(`/inquiries/${inquiryId}/attachments/${attachmentId}`, {
      responseType: 'blob',
    });

    // Create a download link and trigger download
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Accept NDA for an inquiry
  async acceptNda(inquiryId: string): Promise<InquiryNdaAcceptanceDto> {
    return API.post<InquiryNdaAcceptanceDto>(`/inquiries/${inquiryId}/nda-acceptance`).then((res) => res.data);
  },

  // List NDA acceptances for an inquiry (admin only)
  async listNdaAcceptances(inquiryId: string): Promise<InquiryNdaAcceptanceDto[]> {
    return API.get<InquiryNdaAcceptanceDto[]>(`/inquiries/${inquiryId}/nda-acceptances`).then((res) => res.data);
  },
};
